import {
    BaseButton,
    BaseCheckbox,
    BaseInput,
    ButtonGroup,
    DataTable,
    DataTablePresenter,
    DENIALS_RANGES,
    denialsRangeOption,
    DenialWorkerDialog,
    formatDate,
    IButtonGroupItemProps,
    IDenialGroupRow,
    ITableColumn,
    ROUTINE_ADJUSTMENT_LABEL,
    searchDenialRows,
    StatCard,
    StatCardRow,
    TabContainer,
    tabElementId,
    ToastContext,
    visibleDenialRows,
} from '@enter/core';
import {
    mdiAutoFix,
    mdiFilterVariant,
    mdiFormatListChecks,
    mdiMagnify,
    mdiRobotOutline,
    mdiSearchWeb,
    mdiTrayArrowDown,
} from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTableCard, PageLayout, PageLayoutRow, PageLayoutTitle } from 'components';
import { DialogContext } from 'services';
import { IObservedRootStore } from 'stores';
import providerWorkerStore from 'stores/worker.store';
import * as S from './denials.styles';

/**
 * Denials & Rejections (FE-74, with the FE-75 worker action).
 *
 * The table shows what the adjustment report can actually answer — scope, code, the official CARC
 * text, and the number of remits carrying the code — and marks what it cannot. Total charge and Total
 * paid render as pending: the report's `amount` averages per remit instead of summing (measured on
 * production 2026-09-16, see `denial-rows.ts`), so it fills neither. The other missing columns are
 * named under the table. An empty column would read as "no data for this practice" rather than "we
 * cannot ask for this yet" (NS-3, NS-4).
 *
 * The tabs FILTER, and the report behind the table holds denials only — 835 adjustments. A rejection
 * never reaches adjudication and has no CARC, so the Rejections tab cannot reuse these rows: showing
 * them there would present denials as rejections. Until rejections arrive as codes (FE-73 · B1), that
 * tab says so, and the All tab says it holds denials only.
 *
 * The WORKER column is an action, not a status. "Configure worker" opens the rule dialog for that code
 * group; whether an existing rule already covers the group is not shown, because that claim could only
 * be checked against the platform's matcher, one claim at a time (FE-75).
 */

type DenialsTab = 'all' | 'denials' | 'rejections';

const TABS = [
    { id: 'all', label: 'All' },
    { id: 'denials', label: 'Denials' },
    { id: 'rejections', label: 'Rejections' },
];

/** The one panel the tabs control: its content — the table, or the rejections note — follows the active tab. */
const TAB_PANEL_ID = 'denials-tab-panel';

const PENDING = {
    // The design's cards: what is recoverable, what falls off the filing cliff, and how much a worker covers.
    recoverable:
        'Waiting on money per code: the adjustment report returns neither total charge nor total paid (FE-73), and a recoverable figure also needs the claims behind each code (B4, B6).',
    coverage:
        'Waiting on outcome metrics (FE-73 · B8) and on a way to ask the platform matcher which claims a rule already covers (FE-75).',
    // Measured on production 2026-09-16 (denial-rows.ts). Said on the hint so a reader knows why the report's amount is
    // not simply shown here.
    totals:
        'Waiting on total charge and total paid per code (FE-73). The adjustment report’s amount can’t stand in: it averages each remit’s adjustments for the code instead of adding them, so remits with the code on several lines come out low.',
    remarks: 'Waiting on remark codes paired with each reason code (FE-73 · B2).',
    deadline: 'Waiting on a filing deadline per claim (FE-73 · B7).',
};

/** The design's toolbar controls the report cannot back yet — shown, disabled, with the reason. */
const UNAVAILABLE = {
    filters: 'Filters need payer and provider scope on the adjustment report (FE-73 · B5).',
    sort: 'Sorting by deadline needs a filing deadline per claim (FE-73 · B7).',
};

/** The date range control, in the design's order. Its labels map back to ranges in `DENIALS_RANGES`. */
const RANGE_BUTTONS: IButtonGroupItemProps[] = DENIALS_RANGES.map(option => ({ label: option.label }));

/** What the count column counts, on its header — "remits" is billing jargon and the number is easy to misread. */
const REMITS_MEANING =
    'Claim payments (835 remits) carrying this code. A remit with the code on several lines counts once. Not adjustments, and not claims: one claim can have several remits.';

/** Only Code is expressible: the adjustment report takes no payer or provider parameter (FE-73 · B5). */
const GROUP_BY: IButtonGroupItemProps[] = [
    { label: 'Code' },
    {
        label: 'Payer + Code',
        disabled: true,
        title: 'Needs a payer parameter on the adjustment report (FE-73 · B5).',
    },
    {
        label: 'Provider + Code',
        disabled: true,
        title: 'Needs a provider parameter on the adjustment report (FE-73 · B5).',
    },
];

/** The design's "updated" stamp: when this page fetched its report, in the viewer's time zone. */
const UPDATED_FORMAT = 'MMM d yyyy, h:mm a';

export const Denials: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        const [tab, setTab] = React.useState<DenialsTab>('all');
        const [showRoutine, setShowRoutine] = React.useState(false);
        const [query, setQuery] = React.useState('');
        const { accountId } = useParams();
        const navigate = useNavigate();
        const dialogContext = React.useContext(DialogContext);
        const toastContext = React.useContext(ToastContext);

        const store = rootStore?.denialsStore;
        const workersHref = accountId ? `/${accountId}/configuration` : 'configuration';

        React.useEffect(() => {
            // A report that failed is asked for again when the page is opened again, not left as it was.
            if (store && !store.loading && (!store.loaded || store.error)) {
                void store.load();
            }
        }, [store]);

        const openWorkerDialog = (row: IDenialGroupRow): void => {
            dialogContext.showDialog({
                props: { onClose: dialogContext.hideDialog, disableBackdropClick: true },
                Component: (
                    <DenialWorkerDialog
                        code={row.code}
                        reasonDescription={row.reasonDescription}
                        store={providerWorkerStore}
                        workersHref={workersHref}
                        onClose={dialogContext.hideDialog}
                        onActivated={(rule): void => {
                            dialogContext.hideDialog();
                            toastContext.notify && toastContext.notify(`Worker configuration activated: ${rule.name}`);
                        }}
                    />
                ),
            });
        };
        // The columns are memoised once, so their cells reach the latest handler through a ref rather than
        // a closure from the first render.
        const openWorkerDialogRef = React.useRef(openWorkerDialog);
        openWorkerDialogRef.current = openWorkerDialog;

        const columns: ITableColumn<IDenialGroupRow>[] = React.useMemo(
            () => [
                {
                    id: 'Scope',
                    accessor: 'scope',
                    // Widths are NUMBERS. react-table does arithmetic on them: the first version passed '110px', which
                    // became NaN, so every cell fell back to its own content width and no row lined up with another or
                    // with the headers. Fixed columns pin min and max to the width; the two descriptions share the space
                    // left over.
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                    Header: () => <S.TableHeader>Scope</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => <S.TableCell>{row.original.scope}</S.TableCell>,
                },
                {
                    id: 'Codes',
                    accessor: 'codes',
                    width: 130,
                    minWidth: 130,
                    maxWidth: 130,
                    Header: () => <S.TableHeader>Codes</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => <S.CodeChip>{row.original.codes}</S.CodeChip>,
                },
                {
                    id: 'Reason',
                    accessor: 'reasonDescription',
                    // The widths add up to the table's minimum width (useFlexLayout gives the body that min-width and no
                    // column shrinks), and the two descriptions grow into whatever is left. At 480 the seven columns came
                    // to 1,300px and clipped the Worker column in a 1600px window; the eight now come to 1,190px.
                    width: 240,
                    minWidth: 240,
                    maxWidth: 4000,
                    Header: () => <S.TableHeader>Reason code description</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.ReasonCell title={row.original.reasonDescription}>
                            {row.original.reasonDescription}
                        </S.ReasonCell>
                    ),
                },
                {
                    id: 'Remark',
                    accessor: 'key',
                    width: 180,
                    minWidth: 180,
                    maxWidth: 4000,
                    Header: () => <S.TableHeader title={PENDING.remarks}>Remark code description</S.TableHeader>,
                    // Pending: the remark report is not joined to the reason codes (FE-73 · B2).
                    Cell: (): React.ReactElement => <S.PendingText title={PENDING.remarks}>&mdash;</S.PendingText>,
                },
                {
                    id: 'Remits',
                    accessor: 'remits',
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                    // Measured on production 2026-09-16: the report counts the remits carrying a code, not its
                    // adjustments and not claims. A remit carrying two codes counts under both, so the table has no
                    // total row: a sum would overstate the remits.
                    Header: () => <S.TableHeaderRight title={REMITS_MEANING}>Remits</S.TableHeaderRight>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.NumberCell>{row.original.remits.toLocaleString()}</S.NumberCell>
                    ),
                },
                {
                    id: 'TotalCharge',
                    accessor: 'key',
                    width: 130,
                    minWidth: 130,
                    maxWidth: 130,
                    Header: () => <S.TableHeaderRight title={PENDING.totals}>Total charge</S.TableHeaderRight>,
                    // Pending, not zero: the report's `amount` is neither this nor Total paid (denial-rows.ts).
                    Cell: (): React.ReactElement => <S.PendingCell title={PENDING.totals}>&mdash;</S.PendingCell>,
                },
                {
                    id: 'TotalPaid',
                    accessor: 'key',
                    width: 110,
                    minWidth: 110,
                    maxWidth: 110,
                    Header: () => <S.TableHeaderRight title={PENDING.totals}>Total paid</S.TableHeaderRight>,
                    Cell: (): React.ReactElement => <S.PendingCell title={PENDING.totals}>&mdash;</S.PendingCell>,
                },
                {
                    id: 'Worker',
                    accessor: 'key',
                    // Fits the compact "Configure worker" button with the design's worker icon on one line.
                    width: 180,
                    minWidth: 180,
                    maxWidth: 180,
                    Header: () => <S.TableHeaderRight>Worker</S.TableHeaderRight>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.WorkerCell>
                            <BaseButton
                                variant="outlined"
                                size="compact"
                                onClick={(): void => openWorkerDialogRef.current(row.original)}>
                                <Icon path={mdiRobotOutline} size={0.6} />
                                <span>Configure worker</span>
                            </BaseButton>
                        </S.WorkerCell>
                    ),
                },
            ],
            [],
        );

        const visible = store ? visibleDenialRows(store.rows, showRoutine) : [];
        const rows = searchDenialRows(visible, query);

        // One presenter for the life of the page. Built in the JSX, a new one came with every render — every
        // keystroke in the search, every store update — each with a MobX reaction nobody disposed, and the table
        // re-read its saved settings each time. The rows reach it through `onDataUpdate` instead.
        const [presenter] = React.useState(
            () =>
                new DataTablePresenter<IDenialGroupRow>({
                    name: 'denialGroups',
                    data: rows,
                    columns,
                    filterSchemas: [],
                }),
        );

        React.useEffect(() => {
            presenter.onDataUpdate(rows);
            presenter.onDataTotalChange(rows.length);
        }, [presenter, rows]);

        if (!rootStore || !store) {
            return <></>;
        }

        // Routine groups the filter hid. Counted in the subtitle and named on the control that brings them back,
        // so nothing leaves the table silently.
        const hiddenRoutine = showRoutine ? 0 : store.rows.length - visible.length;
        const showsTable = tab !== 'rejections';
        // Demo mode has no denial data to ask for, so there is nothing to search, re-range or retry.
        const isDemo = rootStore.isDemo();
        const rangeOption = denialsRangeOption(store.range);
        const selectedRange = RANGE_BUTTONS.find(button => button.label === rangeOption.label);
        const subtitle = [rootStore.companyName, store.loadedAt && `updated ${formatDate(store.loadedAt, UPDATED_FORMAT)}`]
            .filter(Boolean)
            .join(' · ');

        return (
            <PageLayout>
                <PageLayoutRow>
                    <S.TitleRow>
                        <div>
                            <PageLayoutTitle>Denials &amp; Rejections</PageLayoutTitle>
                            <S.Subtitle>{subtitle || `Reason codes in plain language · ${rangeOption.phrase}`}</S.Subtitle>
                        </div>
                        {/* The design's header row: five small buttons (`.btn.sm`, 28px), the work queue last and
                            primary. Four of them have nothing behind them yet and say so on hover. */}
                        <S.HeaderActions>
                            <S.ActionSlot title="The investigation explorer is not built yet (FE-77).">
                                <BaseButton variant="outlined" size="compact" disabled>
                                    <Icon path={mdiSearchWeb} size={0.7} />
                                    <span>Investigate</span>
                                </BaseButton>
                            </S.ActionSlot>
                            <BaseButton
                                variant="outlined"
                                size="compact"
                                onClick={(): void => {
                                    navigate(workersHref);
                                }}>
                                <Icon path={mdiRobotOutline} size={0.7} />
                                <span>Workers</span>
                            </BaseButton>
                            <S.ActionSlot title="Export needs the columns it exports.">
                                <BaseButton variant="outlined" size="compact" disabled>
                                    <Icon path={mdiTrayArrowDown} size={0.7} />
                                    <span>Export</span>
                                </BaseButton>
                            </S.ActionSlot>
                            <S.ActionSlot title="Coder rule requests are wired in FE-76.">
                                <BaseButton variant="outlined" size="compact" disabled>
                                    <Icon path={mdiAutoFix} size={0.7} />
                                    <span>Request Coder Rule</span>
                                </BaseButton>
                            </S.ActionSlot>
                            <S.ActionSlot title="The claim work queue needs the group → claims drill-down (FE-73 · B6), and is built in FE-76.">
                                <BaseButton variant="primary" size="compact" disabled>
                                    <Icon path={mdiFormatListChecks} size={0.7} />
                                    <span>Work Queue</span>
                                </BaseButton>
                            </S.ActionSlot>
                        </S.HeaderActions>
                    </S.TitleRow>
                </PageLayoutRow>

                <PageLayoutRow>
                    {/* The design's three cards. Each still shows a dash and the reason it cannot be filled: the
                        figures behind them are money per code, a filing deadline, and what a worker rule covers. */}
                    <StatCardRow>
                        <StatCard label="Recoverable" sub={rangeOption.phrase} unavailableReason={PENDING.recoverable} />
                        <StatCard
                            label="Filing cliff < 14d"
                            sub="lost if missed"
                            tone="urgent"
                            unavailableReason={PENDING.deadline}
                        />
                        <StatCard label="Worker coverage" sub="claims covered" unavailableReason={PENDING.coverage} />
                    </StatCardRow>
                </PageLayoutRow>

                <PageLayoutRow>
                    <TabContainer
                        variant="underline"
                        tabs={TABS}
                        activeTab={tab}
                        onTabChange={(id): void => setTab(id as DenialsTab)}
                        panelId={TAB_PANEL_ID}
                        trailing={
                            // Kept when a load fails: a shorter range is one way out of a report that failed.
                            showsTable && !isDemo ? (
                                <S.Toolbar>
                                    <S.SearchSlot>
                                        <BaseInput
                                            compact
                                            iconPath={mdiMagnify}
                                            value={query}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                                                setQuery(e.target.value)
                                            }
                                            placeholder="Search codes or descriptions"
                                            aria-label="Search codes or descriptions"
                                            // The search filters rows already on the page and is never sent, so the
                                            // Latin-1 check for what the backend accepts does not apply.
                                            disableLatin1Validation
                                        />
                                    </S.SearchSlot>
                                    <ButtonGroup
                                        variant="track"
                                        buttons={RANGE_BUTTONS}
                                        selected={selectedRange}
                                        onSelect={(button): void => {
                                            const option = DENIALS_RANGES.find(o => o.label === button.label);
                                            if (option) {
                                                store.setRange(option.range);
                                            }
                                        }}
                                    />
                                    <S.ActionSlot title={UNAVAILABLE.filters}>
                                        <BaseButton variant="outlined" size="compact" disabled>
                                            <Icon path={mdiFilterVariant} size={0.6} />
                                            <span>Filters</span>
                                        </BaseButton>
                                    </S.ActionSlot>
                                    <S.ActionSlot title={UNAVAILABLE.sort}>
                                        <BaseButton variant="outlined" size="compact" disabled>
                                            Sort: Deadline
                                        </BaseButton>
                                    </S.ActionSlot>
                                </S.Toolbar>
                            ) : undefined
                        }
                    />
                </PageLayoutRow>

                <PageLayoutRow>
                    <S.TabPanel role="tabpanel" id={TAB_PANEL_ID} aria-labelledby={tabElementId(TAB_PANEL_ID, tab)}>
                        {showsTable ? (
                            <DataTableCard size="large">
                                <S.SectionRow>
                                    <div>
                                        <S.SectionTitle>Adjustment Table by Reason Code</S.SectionTitle>
                                        <S.SectionSub>
                                            {/* No counts while the report has failed: the rows still held belong to the
                                                window that loaded before, not the one named here (NS-3). */}
                                            {!store.error && `${rows.length} ${rows.length === 1 ? 'group' : 'groups'} · `}
                                            official CARC text · {rangeOption.phrase}
                                            {tab === 'all' && ' · denials only'}
                                            {!store.error &&
                                                store.skipped > 0 &&
                                                ` · ${store.skipped} report ${
                                                    store.skipped === 1 ? 'row' : 'rows'
                                                } could not be read as a denial code`}
                                            {!store.error &&
                                                hiddenRoutine > 0 &&
                                                ` · ${hiddenRoutine} routine ${
                                                    hiddenRoutine === 1 ? 'group' : 'groups'
                                                } hidden`}
                                        </S.SectionSub>
                                    </div>
                                    <S.SectionControls>
                                        {/* Settled 2026-09-11: routine adjustments on PAID claims are hidden by
                                            default (see routine-adjustments.ts). The control names exactly what it
                                            hides. It filters this table, so it sits with the table's own controls. */}
                                        <BaseCheckbox
                                            variant="small"
                                            checked={showRoutine}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                                                setShowRoutine(e.target.checked)
                                            }
                                            label={`Show routine adjustments (${ROUTINE_ADJUSTMENT_LABEL})`}
                                        />
                                        <S.GroupBy>
                                            <S.GroupByLabel>Group by</S.GroupByLabel>
                                            <ButtonGroup variant="pills" buttons={GROUP_BY} selected={GROUP_BY[0]} />
                                        </S.GroupBy>
                                    </S.SectionControls>
                                </S.SectionRow>

                                {store.error ? (
                                    <S.Notice>
                                        <span>{store.error}</span>
                                        {!isDemo && (
                                            <BaseButton
                                                variant="outlined"
                                                size="compact"
                                                onClick={(): void => {
                                                    void store.load();
                                                }}>
                                                Try again
                                            </BaseButton>
                                        )}
                                    </S.Notice>
                                ) : (
                                    <S.TableFrame>
                                        <DataTable
                                            presenter={presenter}
                                            isLoading={store.loading}
                                            // The rows come biggest first, and nothing here can re-sort them: the
                                            // table sorts through a fetch this page does not have. Left on, a header
                                            // click would show a sort arrow over rows that did not move.
                                            disableSorting={true}
                                            hideFiltering={true}
                                            // Without this the table draws an empty controls panel above its header —
                                            // the blank band between the section title and the columns.
                                            hideNavigation={true}
                                            hidePagination={true}
                                            hideSettings={true}
                                        />
                                    </S.TableFrame>
                                )}

                                <S.MissingColumns>
                                    {tab === 'all' &&
                                        'Rejections are not in this table: grouping them needs structured status ' +
                                            'codes (FE-73 · B1). '}
                                    Not shown yet, because the reporting endpoints cannot answer them: a distinct
                                    claim count and the filing deadline. The remark code, total charge and total paid
                                    columns are waiting on the reports. Tracked on FE-73. &ldquo;Configure
                                    worker&rdquo; creates a rule for a group; which existing rules already cover a
                                    group is not shown until that can be checked against the platform&apos;s matcher.
                                </S.MissingColumns>
                            </DataTableCard>
                        ) : (
                            <S.PendingPanel>
                                <S.PendingTitle>Rejections are not grouped yet</S.PendingTitle>
                                <S.PendingBody>
                                    A rejection never reaches adjudication, so it carries no CARC. It is identified by
                                    a claim status category and status code from the 277CA or 999 acknowledgement
                                    &mdash; A7:562, for example. The transmission status report returns rejections as
                                    free-text messages with a count, so grouping them here would mean inventing the
                                    code. Waiting on structured rejection codes (FE-73 · B1).
                                </S.PendingBody>
                            </S.PendingPanel>
                        )}
                    </S.TabPanel>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);
